import React from 'react';
import { ChevronDownIcon, TrashIcon } from '@heroicons/react/24/outline';

import {
  AcordFormConfigurationFieldsFragment,
  AcordFormConfigurationInput,
  AcordAppendType,
  AcordFormSection,
  AcordNumberFormat,
  AcordProducerInformationSource,
  AgencyFieldsFragment,
  DescriptionOfOperationsMode
} from '~/api/graphql';
import { Description, ErrorMessage, Field, Fieldset, Label } from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Switch, SwitchField } from '@oysterjs/ui/switch';
import { Radio, RadioField, RadioGroup } from '@oysterjs/ui/radio';
import { AutoResizeTextarea } from '@oysterjs/ui/textarea';
import { Button } from '@oysterjs/ui/button';
import { Badge } from '@oysterjs/ui/badge';
import { MultiListbox, MultiListboxLabel, MultiListboxOption } from '@oysterjs/ui/multiselect';
import clsx from 'clsx';

import {
  APPEND_FORMS,
  APPEND_TYPE_LABELS,
  appendTypeOptionsForForm,
  base64ImageMime,
  buildConfigurationInput,
  COIEOIState,
  DOO_MODE_LABELS,
  FORM_LABELS,
  isDOOScopedRuleDisabled,
  newRuleRow,
  presetToCustomRow,
  PRODUCER_INFORMATION_SOURCE_LABELS,
  reconcileAppendTypes,
  RULE_FORMS,
  RuleErrors,
  RuleRow,
  SECTION_DESCRIPTIONS,
  SECTION_LABELS,
  sectionsForForm,
  stateFromConfiguration,
  validateRules,
  validateLogoFile,
  validateLogoDimensions,
  dataUrlToBase64
} from './coi_eoi_section.utils';

const isActiveRule = (rule: RuleRow): boolean => !rule.isPreset || rule.enabled;

const formatSectionRuleSummary = (sectionRules: { rule: RuleRow }[]): string => {
  const total = sectionRules.length;
  if (total === 0) {
    return 'No rules';
  }
  const enabled = sectionRules.filter(({ rule }) => isActiveRule(rule)).length;
  if (enabled === total) {
    return total === 1 ? '1 rule' : `${total} rules`;
  }
  return `${enabled} of ${total} enabled`;
};

// Imperative handle the parent (agency settings) drives. The section no longer
// owns a save button — it reports dirty state via onDirtyChange and exposes its
// input + validation so the page-level save bar can fold COI/EOI into the single
// updateAgency call alongside the agency fields.
export interface COIEOISectionHandle {
  // The current configuration input, ready to attach to the updateAgency mutation.
  getInput: () => AcordFormConfigurationInput;
  // Run client-side validation, surfacing inline errors; returns true when valid.
  validate: () => boolean;
  // Revert all edits back to the last saved state.
  discard: () => void;
  // Mark the current state as saved (clears the dirty flag after a successful save).
  markSaved: (configuration?: AcordFormConfigurationFieldsFragment) => void;
}

interface COIEOISectionProps {
  agency: AgencyFieldsFragment;
  // The Cara default; agency null overrides resolve to the matching field here so
  // the editor shows effective values and pre-populates preset rules.
  defaultConfiguration: AcordFormConfigurationFieldsFragment;
  // Called whenever the section's unsaved-changes state flips, so the page-level
  // save bar knows to appear and include this section in the save.
  onDirtyChange?: (dirty: boolean) => void;
}

export const COIEOISection = React.forwardRef<COIEOISectionHandle, COIEOISectionProps>(
  (props, ref) => {
    const { onDirtyChange } = props;

    const initialState = React.useMemo(
      () =>
        stateFromConfiguration(
          props.agency.configuration.acordFormConfiguration,
          props.defaultConfiguration
        ),
      [props.agency.configuration.acordFormConfiguration, props.defaultConfiguration]
    );

    const [state, setState] = React.useState<COIEOIState>(initialState);
    const [savedState, setSavedState] = React.useState<COIEOIState>(initialState);
    const [expandedSections, setExpandedSections] = React.useState<Set<AcordFormSection>>(
      () => new Set()
    );
    const [changesPending, setChangesPending] = React.useState(false);
    const changesPendingRef = React.useRef(false);
    const [ruleErrors, setRuleErrors] = React.useState<Record<number, RuleErrors>>({});
    const [logoError, setLogoError] = React.useState<string | null>(null);
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Re-sync clean state when the loader returns a fresh configuration. While
    // edits are pending, preserve them but update the discard baseline to the
    // latest server state so unrelated loader revalidation cannot wipe local work.
    React.useEffect(() => {
      setSavedState(initialState);
      if (!changesPendingRef.current) {
        setState(initialState);
        setExpandedSections(new Set());
      }
    }, [initialState]);

    const setSectionExpanded = React.useCallback((section: AcordFormSection, open: boolean) => {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        if (open) {
          next.add(section);
        } else {
          next.delete(section);
        }
        return next;
      });
    }, []);

    // Report dirty state up so the page-level save bar can show/hide.
    React.useEffect(() => {
      onDirtyChange?.(changesPending);
    }, [changesPending, onDirtyChange]);

    const update = (updater: (prev: COIEOIState) => COIEOIState) => {
      changesPendingRef.current = true;
      setState(updater);
      setChangesPending(true);
    };

    const updateRule = (index: number, patch: Partial<RuleRow>) => {
      update((prev) => ({
        ...prev,
        rules: prev.rules.map((rule, i) => (i === index ? { ...rule, ...patch } : rule))
      }));
      // Clear the inline error for any field the user is now editing.
      setRuleErrors((prev) => {
        if (!prev[index]) return prev;
        const next = { ...prev };
        const current = { ...next[index] };
        if ('condition' in patch) delete current.condition;
        if ('phrasing' in patch) delete current.phrasing;
        if (current.condition || current.phrasing) next[index] = current;
        else delete next[index];
        return next;
      });
    };

    const handleLogoFile = async (file: File | undefined) => {
      if (!file) return;
      const fileError = validateLogoFile(file);
      if (fileError) {
        setLogoError(fileError);
        return;
      }
      const dimensionError = await validateLogoDimensions(file);
      if (dimensionError) {
        setLogoError(dimensionError);
        return;
      }
      setLogoError(null);
      const reader = new FileReader();
      reader.onload = () => {
        update((prev) => ({
          ...prev,
          idCardLogoBase64Data: dataUrlToBase64(String(reader.result))
        }));
      };
      reader.readAsDataURL(file);
    };

    const validate = (): boolean => {
      const errors = validateRules(state.rules);
      setRuleErrors(errors);
      if (Object.keys(errors).length > 0) {
        setExpandedSections((prev) => {
          const next = new Set(prev);
          for (const indexStr of Object.keys(errors)) {
            const section = state.rules[Number(indexStr)]?.section;
            if (section) {
              next.add(section);
            }
          }
          return next;
        });
      }
      return Object.keys(errors).length === 0;
    };

    const discard = () => {
      changesPendingRef.current = false;
      setState(savedState);
      setExpandedSections(new Set());
      setRuleErrors({});
      setLogoError(null);
      setChangesPending(false);
    };

    React.useImperativeHandle(
      ref,
      () => ({
        getInput: () => buildConfigurationInput(state),
        validate,
        discard,
        markSaved: (configuration?: AcordFormConfigurationFieldsFragment) => {
          const nextState = configuration
            ? stateFromConfiguration(configuration, props.defaultConfiguration)
            : state;
          changesPendingRef.current = false;
          setState(nextState);
          setSavedState(nextState);
          setExpandedSections(new Set());
          setRuleErrors({});
          setLogoError(null);
          setChangesPending(false);
        }
      }),
      // validate/discard close over state/savedState, so refresh the handle when either changes.
      [state, savedState] // eslint-disable-line react-hooks/exhaustive-deps
    );

    return (
      <div className="space-y-6">
        <div className="rounded-lg bg-white dark:bg-neutral-800 shadow">
          <div className="px-4 py-6 sm:px-6 space-y-10">
            {/* Header */}
            <div>
              <h4 className="mt-0 font-body font-semibold text-neutral-900 dark:text-white">
                ACORD Form Settings
              </h4>
              <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">
                Configure how Cara generates ACORD certificates, evidence forms, and related
                documents for your agency.
              </p>
            </div>

            {/* Producer information source */}
            <SettingRow
              title="Producer Information Source"
              description="Where producer/authorized-representative and producer contact fields come from."
            >
              <Fieldset className="max-w-xl">
                <RadioGroup
                  value={state.producerInformationSource}
                  onChange={(value: AcordProducerInformationSource) =>
                    update((prev) => ({
                      ...prev,
                      producerInformationSource: value
                    }))
                  }
                  aria-label="Producer information source"
                >
                  <RadioField>
                    <Radio color="sky" value={AcordProducerInformationSource.ConfiguredDefault} />
                    <Label>
                      {
                        PRODUCER_INFORMATION_SOURCE_LABELS[
                          AcordProducerInformationSource.ConfiguredDefault
                        ]
                      }
                    </Label>
                    <Description>
                      Use your agency or user default Producer Information for every field.
                    </Description>
                  </RadioField>
                  <RadioField>
                    <Radio
                      color="sky"
                      value={AcordProducerInformationSource.CaraFromInstructions}
                    />
                    <Label>
                      {
                        PRODUCER_INFORMATION_SOURCE_LABELS[
                          AcordProducerInformationSource.CaraFromInstructions
                        ]
                      }
                    </Label>
                    <Description>
                      Keep the agency name and address fixed, but allow workspace instructions to
                      set the producer name and producer contact details. When instructions do not
                      specify them, Cara uses the current workspace user&apos;s name and email;
                      configured phone and fax values remain the fallback.
                    </Description>
                  </RadioField>
                </RadioGroup>
              </Fieldset>
            </SettingRow>

            {/* PDF append rules */}
            <SettingRow
              title="PDF Append Rules"
              description="Which referenced pages are automatically appended to each generated form's PDF. Use “Never append” to append nothing."
            >
              <Fieldset className="flex flex-col gap-y-4">
                {APPEND_FORMS.map((form) => (
                  <Field key={form} className="max-w-xl">
                    <Label>{FORM_LABELS[form]}</Label>
                    <MultiListbox
                      aria-label={`${FORM_LABELS[form]} append types`}
                      placeholder="Select append types…"
                      value={state.appendTypesByForm[form]}
                      onChange={(next: AcordAppendType[]) =>
                        update((prev) => ({
                          ...prev,
                          appendTypesByForm: {
                            ...prev.appendTypesByForm,
                            [form]: reconcileAppendTypes(prev.appendTypesByForm[form], next)
                          }
                        }))
                      }
                    >
                      {appendTypeOptionsForForm(form).map((type) => (
                        <MultiListboxOption key={type} value={type}>
                          <MultiListboxLabel>{APPEND_TYPE_LABELS[type]}</MultiListboxLabel>
                        </MultiListboxOption>
                      ))}
                    </MultiListbox>
                  </Field>
                ))}
              </Fieldset>
            </SettingRow>

            {/* Number formatting */}
            <SettingRow
              title="Number Formatting"
              description="How numbers are formatted inside limit boxes on ACORD 24, 25, 27, and 28."
            >
              <SwitchField>
                <Label>Use comma separators</Label>
                <Description>
                  When on, limit amounts render as 1,000,000. When off, as 1000000.
                </Description>
                <Switch
                  color="sky"
                  aria-label="Use comma separators"
                  checked={state.numberFormat === AcordNumberFormat.Commas}
                  onChange={(checked: boolean) =>
                    update((prev) => ({
                      ...prev,
                      numberFormat: checked ? AcordNumberFormat.Commas : AcordNumberFormat.NoCommas
                    }))
                  }
                />
              </SwitchField>
            </SettingRow>

            {/* ID card logo */}
            <SettingRow
              title="ID Card Logo"
              description="Your agency logo, stamped in the available whitespace on ACORD 50 ID cards."
            >
              <Field>
                <Label>Logo image</Label>
                <Description>
                  PNG or JPG, up to 1024×1024 pixels and 1 MB. Leave empty for no logo.
                </Description>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  aria-label="ID card logo"
                  className="hidden"
                  onChange={(e) => {
                    handleLogoFile(e.currentTarget.files?.[0]);
                    // Reset so re-selecting the same file still fires onChange.
                    e.currentTarget.value = '';
                  }}
                />
                {state.idCardLogoBase64Data ? (
                  <div className="mt-2 flex items-center gap-3">
                    <img
                      src={`data:${base64ImageMime(state.idCardLogoBase64Data)};base64,${state.idCardLogoBase64Data}`}
                      alt="Agency ID card logo"
                      className="max-h-16 object-contain rounded border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 p-1"
                    />
                    <Button type="button" outline onClick={() => fileInputRef.current?.click()}>
                      Replace
                    </Button>
                    <Button
                      type="button"
                      outline
                      onClick={() => {
                        setLogoError(null);
                        update((prev) => ({ ...prev, idCardLogoBase64Data: null }));
                      }}
                    >
                      Remove
                    </Button>
                  </div>
                ) : (
                  <div className="mt-2">
                    <Button type="button" outline onClick={() => fileInputRef.current?.click()}>
                      Upload logo
                    </Button>
                  </div>
                )}
                {logoError && <ErrorMessage>{logoError}</ErrorMessage>}
              </Field>
            </SettingRow>

            {/* Description of Operations mode — placed next to Rules since the DOO
              mode governs whether DOO-scoped rules below are applied. */}
            <SettingRow
              title="Description of Operations"
              description="How the Description of Operations box on ACORD 25 is populated."
            >
              <Field className="max-w-md">
                <Label>DOO mode</Label>
                <Select
                  aria-label="Description of Operations mode"
                  value={state.descriptionOfOperationsMode}
                  onChange={(e) => {
                    const mode = e.currentTarget.value as DescriptionOfOperationsMode;
                    update((prev) => ({
                      ...prev,
                      descriptionOfOperationsMode: mode
                    }));
                  }}
                >
                  {Object.values(DescriptionOfOperationsMode).map((mode) => (
                    <option key={mode} value={mode}>
                      {DOO_MODE_LABELS[mode]}
                    </option>
                  ))}
                </Select>
              </Field>
            </SettingRow>

            {/* Rules engine — grouped by fixed ACORD form and section. Presets are
              read-only and toggled on/off; custom rules are editable and deletable.
              Toggled-off presets are omitted on save. Unsupported stored rules stay
              in state but are not shown in the grouped editor. */}
            <SettingRow
              title="Rules"
              description="Condition-and-phrasing pairs scoped to a form section. Cara injects the phrasing when the condition is met. Presets can be toggled on or off and copied to a custom rule. Use [[placeholder]] for values the model fills in."
            >
              <div className="flex flex-col gap-y-6">
                {RULE_FORMS.map((form) => (
                  <div key={form} className="flex flex-col gap-y-3">
                    <h5 className="font-body font-semibold text-neutral-900 dark:text-white">
                      {FORM_LABELS[form]}
                    </h5>

                    {sectionsForForm(form).map((section) => {
                      const sectionSuppressed = isDOOScopedRuleDisabled(
                        state.descriptionOfOperationsMode,
                        section
                      );
                      const sectionRules = state.rules
                        .map((rule, index) => ({ rule, index }))
                        .filter(({ rule }) => rule.formType === form && rule.section === section);
                      const sectionSummary = formatSectionRuleSummary(sectionRules);
                      const isExpanded = expandedSections.has(section);
                      const panelId = `rule-section-panel-${section}`;

                      return (
                        <div
                          key={section}
                          className="rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-800 shadow-sm"
                        >
                          <button
                            type="button"
                            data-testid={`rule-section-${section}`}
                            aria-expanded={isExpanded}
                            aria-controls={panelId}
                            className="flex w-full items-center gap-3 p-4 text-left hover:bg-neutral-50 dark:hover:bg-neutral-900/50 transition-colors"
                            onClick={() => setSectionExpanded(section, !isExpanded)}
                          >
                            <ChevronDownIcon
                              aria-hidden="true"
                              className={clsx(
                                'h-5 w-5 shrink-0 text-neutral-400 dark:text-neutral-500 transition-transform duration-200',
                                isExpanded ? 'rotate-180' : ''
                              )}
                            />
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="text-sm font-semibold text-neutral-900 dark:text-white">
                                  {SECTION_LABELS[section]}
                                </span>
                                <span className="text-xs text-neutral-500 dark:text-neutral-400">
                                  {sectionSummary}
                                </span>
                                {sectionSuppressed && (
                                  <Badge color="amber">Not applied in current DOO mode</Badge>
                                )}
                              </div>
                              {!isExpanded && (
                                <p className="mt-1 text-xs text-neutral-500 dark:text-neutral-400 line-clamp-2">
                                  {SECTION_DESCRIPTIONS[section]}
                                </p>
                              )}
                            </div>
                          </button>

                          {isExpanded && (
                            <div
                              id={panelId}
                              className="border-t border-neutral-200 dark:border-neutral-700 px-4 pb-4 pt-4 flex flex-col gap-y-3"
                            >
                              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                                {SECTION_DESCRIPTIONS[section]}
                              </p>

                              {sectionSuppressed && (
                                <p
                                  role="status"
                                  className="text-sm text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 rounded-md px-3 py-2"
                                >
                                  Description of Operations rules are not applied while the DOO mode
                                  is {DOO_MODE_LABELS[state.descriptionOfOperationsMode]}.
                                </p>
                              )}

                              {sectionRules.map(({ rule, index }, sectionRuleIndex) => {
                                const greyed =
                                  sectionSuppressed || (rule.isPreset && !rule.enabled);
                                const rulePosition = sectionRuleIndex + 1;
                                const ruleScope = `${FORM_LABELS[form]} ${SECTION_LABELS[section]}`;

                                return (
                                  <div
                                    key={`${rule.id ?? 'new'}-${index}`}
                                    className={clsx(
                                      'rounded-lg border border-neutral-200 dark:border-neutral-700 p-4 flex flex-col gap-y-3',
                                      greyed && 'opacity-50'
                                    )}
                                  >
                                    <div className="flex items-center gap-2">
                                      {rule.isPreset ? (
                                        <Badge color="sky">Preset</Badge>
                                      ) : (
                                        <Badge color="zinc">Custom</Badge>
                                      )}
                                      <div className="ml-auto flex items-center gap-2">
                                        {rule.isPreset ? (
                                          <Switch
                                            color="sky"
                                            aria-label={`Enable preset ${rulePosition} in ${ruleScope}`}
                                            disabled={sectionSuppressed && !rule.enabled}
                                            checked={rule.enabled}
                                            onChange={(checked: boolean) =>
                                              updateRule(index, { enabled: checked })
                                            }
                                          />
                                        ) : (
                                          <button
                                            type="button"
                                            aria-label={`Remove custom rule ${rulePosition} from ${ruleScope}`}
                                            className="text-neutral-400 dark:text-neutral-500 hover:text-red-600 dark:hover:text-red-400"
                                            onClick={() => {
                                              update((prev) => ({
                                                ...prev,
                                                rules: prev.rules.filter((_, i) => i !== index)
                                              }));
                                              setRuleErrors({});
                                            }}
                                          >
                                            <TrashIcon className="size-4" />
                                          </button>
                                        )}
                                      </div>
                                    </div>

                                    <Field>
                                      <Label>Condition</Label>
                                      <Input
                                        readOnly={rule.isPreset}
                                        disabled={sectionSuppressed}
                                        invalid={!!ruleErrors[index]?.condition}
                                        value={rule.condition}
                                        placeholder={
                                          rule.isPreset ? undefined : 'When the rule applies…'
                                        }
                                        onChange={(e) =>
                                          updateRule(index, { condition: e.currentTarget.value })
                                        }
                                      />
                                      {ruleErrors[index]?.condition && (
                                        <ErrorMessage>{ruleErrors[index].condition}</ErrorMessage>
                                      )}
                                    </Field>
                                    <Field>
                                      <Label>Phrasing</Label>
                                      <AutoResizeTextarea
                                        readOnly={rule.isPreset}
                                        disabled={sectionSuppressed}
                                        invalid={!!ruleErrors[index]?.phrasing}
                                        value={rule.phrasing}
                                        placeholder={
                                          rule.isPreset
                                            ? undefined
                                            : 'Exact text to inject, with optional [[placeholder]] slots…'
                                        }
                                        onChange={(e) =>
                                          updateRule(index, { phrasing: e.currentTarget.value })
                                        }
                                      />
                                      {ruleErrors[index]?.phrasing && (
                                        <ErrorMessage>{ruleErrors[index].phrasing}</ErrorMessage>
                                      )}
                                    </Field>

                                    {rule.isPreset && (
                                      <div>
                                        <Button
                                          type="button"
                                          outline
                                          aria-label={`Copy preset ${rulePosition} to custom rule in ${ruleScope}`}
                                          disabled={sectionSuppressed}
                                          onClick={() =>
                                            update((prev) => ({
                                              ...prev,
                                              rules: [...prev.rules, presetToCustomRow(rule)]
                                            }))
                                          }
                                        >
                                          Copy to custom rule
                                        </Button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}

                              <div>
                                <Button
                                  type="button"
                                  outline
                                  aria-label={`Add rule to ${FORM_LABELS[form]} ${SECTION_LABELS[section]}`}
                                  disabled={sectionSuppressed}
                                  onClick={() =>
                                    update((prev) => ({
                                      ...prev,
                                      rules: [...prev.rules, newRuleRow(form, section)]
                                    }))
                                  }
                                >
                                  Add rule
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </SettingRow>
          </div>
        </div>
      </div>
    );
  }
);

COIEOISection.displayName = 'COIEOISection';

const SettingRow = (props: { title: string; description: string; children: React.ReactNode }) => (
  <div className="grid grid-cols-1 gap-x-8 gap-y-6 md:grid-cols-3">
    <div>
      <h5 className="font-body font-semibold text-neutral-900 dark:text-white">{props.title}</h5>
      <p className="mt-1 text-sm/6 text-neutral-600 dark:text-neutral-400">{props.description}</p>
    </div>
    <div className="col-span-2">{props.children}</div>
  </div>
);
